/**
 * GenBank ストリーム取込（I21 / U152）。
 * 注釈は読みながらシンクへ流し、ORIGIN は行バイトのまま塩基コードへ変換する（全文 string 化禁止・§0.1）。
 * 複数レコードは配列複数として取り込む。
 */

import { MAX_SEQUENCE_LENGTH } from '../constants';
import { GenomicFeatureV1, Strand } from '../annotation/types';
import { Topology } from '../sequence/interval';
import { BYTE_TO_CODE, NUCLEOTIDE_CODE_COUNT } from '../sequence/nucleotideCodes';
import { iterateLineBytes } from './byteStream';
import { parseGenBankLocation } from './genbankLocation';
import { ImportProgress, ImportResult, ImportSink } from './importSink';

const CODE_BUFFER_SIZE = 1 << 16;
const DEFAULT_MAX_FEATURES = 1_000_000;
/** 注釈名に使う qualifier の優先順 */
const NAME_QUALIFIERS = ['gene', 'locus_tag', 'label', 'product', 'standard_name', 'note'];

export interface GenBankParseOptions {
  totalBytes?: number;
  maxSequenceLength?: number;
  maxFeatures?: number;
  onProgress?: (progress: ImportProgress) => void;
  progressIntervalBytes?: number;
}

interface PendingFeature {
  key: string;
  location: string;
  qualifiers: Record<string, string>;
}

export async function parseGenBankStream(
  source: AsyncIterable<Uint8Array>,
  sink: ImportSink,
  options: GenBankParseOptions = {}
): Promise<ImportResult> {
  const maxLength = options.maxSequenceLength ?? MAX_SEQUENCE_LENGTH;
  const maxFeatures = options.maxFeatures ?? DEFAULT_MAX_FEATURES;
  const progressInterval = options.progressIntervalBytes ?? 8 * 1024 * 1024;
  const decoder = new TextDecoder();
  const warnings: string[] = [];

  const codeBuffer = new Uint8Array(CODE_BUFFER_SIZE);
  let codeFilled = 0;

  let bytesRead = 0;
  let lastProgressAt = 0;
  let sequenceCount = 0;

  let mode: 'header' | 'features' | 'origin' = 'header';
  let opened = false;
  let name = '';
  let description = '';
  let declaredLength = 0;
  let topology: Topology = 'linear';
  let basesRead = 0;
  let featureCount = 0;
  let featureLimitWarned = false;

  let pending: PendingFeature | null = null;
  let openQualifier: string | null = null;
  let definitionOpen = false;

  const flushCodes = async () => {
    if (codeFilled === 0) return;
    await sink.appendCodes(codeBuffer.subarray(0, codeFilled));
    codeFilled = 0;
  };

  const reportProgress = () => {
    options.onProgress?.({
      bytesRead,
      basesRead,
      totalBytes: options.totalBytes,
      sequenceName: name,
    });
  };

  const resetRecord = () => {
    mode = 'header';
    opened = false;
    name = '';
    description = '';
    declaredLength = 0;
    topology = 'linear';
    basesRead = 0;
    featureCount = 0;
    featureLimitWarned = false;
    pending = null;
    openQualifier = null;
    definitionOpen = false;
  };

  const openSequence = async () => {
    if (opened) return;
    await sink.beginSequence({
      name: name || `記録 ${sequenceCount + 1}`,
      description,
      topology,
    });
    opened = true;
    sequenceCount += 1;
  };

  const emitPendingFeature = async () => {
    if (!pending) return;
    const current = pending;
    pending = null;
    openQualifier = null;

    if (featureCount >= maxFeatures) {
      if (!featureLimitWarned) {
        warnings.push(`注釈が多いため ${maxFeatures} 件で打ち切りました`);
        featureLimitWarned = true;
      }
      return;
    }

    const location = parseGenBankLocation(current.location, declaredLength, topology);
    if (!location) {
      warnings.push(`位置を解釈できない注釈を飛ばしました: ${current.key} ${current.location}`);
      return;
    }

    const strand: Strand = location.strand;
    const featureName =
      NAME_QUALIFIERS.map((key) => current.qualifiers[key]).find((value) => value && value.length > 0) ??
      current.key;

    const feature: GenomicFeatureV1 = {
      id: `${current.key}-${featureCount + 1}`,
      type: current.key,
      name: featureName,
      start: location.start,
      end: location.end,
      strand,
      exons: location.exons,
      qualifiers: Object.keys(current.qualifiers).length > 0 ? current.qualifiers : undefined,
    };

    await openSequence();
    await sink.addFeature(feature);
    featureCount += 1;
  };

  const finishRecord = async () => {
    await emitPendingFeature();
    if (!opened && (declaredLength > 0 || name)) await openSequence();
    if (!opened) return;
    await flushCodes();
    if (basesRead === 0) {
      warnings.push(`配列本体（ORIGIN）が無い記録です: ${name}`);
    } else if (declaredLength > 0 && basesRead !== declaredLength) {
      warnings.push(`LOCUS の長さ ${declaredLength} bp と本体 ${basesRead} bp が一致しません: ${name}`);
    }
    await sink.endSequence({ length: basesRead, topology });
    resetRecord();
  };

  const handleHeaderLine = (line: string) => {
    if (line.startsWith('LOCUS')) {
      const rest = line.slice('LOCUS'.length).trim();
      const tokens = rest.split(/\s+/);
      name = tokens[0] ?? '';
      const bpAt = tokens.findIndex((token) => token === 'bp' || token === 'aa');
      if (bpAt > 0) declaredLength = Number(tokens[bpAt - 1]) || 0;
      if (/\bcircular\b/i.test(rest)) topology = 'circular';
      definitionOpen = false;
      return;
    }
    if (line.startsWith('DEFINITION')) {
      description = line.slice('DEFINITION'.length).trim();
      definitionOpen = true;
      return;
    }
    if (definitionOpen && line.startsWith(' ')) {
      description = `${description} ${line.trim()}`.trim();
      return;
    }
    definitionOpen = false;
  };

  const handleFeatureLine = async (line: string) => {
    const isNewFeature = line.length > 5 && line.slice(0, 5).trim() === '' && line[5] !== ' ';
    if (isNewFeature) {
      await emitPendingFeature();
      const key = line.slice(5, 21).trim();
      pending = { key, location: line.slice(21).trim(), qualifiers: {} };
      return;
    }

    const trimmed = line.trim();
    if (!pending || trimmed.length === 0) return;

    if (openQualifier) {
      const value = `${pending.qualifiers[openQualifier]} ${trimmed}`;
      if (trimmed.endsWith('"')) {
        pending.qualifiers[openQualifier] = value.slice(0, -1).trim();
        openQualifier = null;
      } else {
        pending.qualifiers[openQualifier] = value;
      }
      return;
    }

    if (trimmed.startsWith('/')) {
      const equalsAt = trimmed.indexOf('=');
      if (equalsAt === -1) {
        pending.qualifiers[trimmed.slice(1)] = 'true';
        return;
      }
      const key = trimmed.slice(1, equalsAt);
      let value = trimmed.slice(equalsAt + 1).trim();
      if (value.startsWith('"')) {
        value = value.slice(1);
        if (value.endsWith('"')) {
          value = value.slice(0, -1);
        } else {
          openQualifier = key;
        }
      }
      pending.qualifiers[key] = value;
      return;
    }

    pending.location += trimmed;
  };

  const appendOriginLine = async (bytes: Uint8Array) => {
    for (let i = 0; i < bytes.length; i += 1) {
      const code = BYTE_TO_CODE[bytes[i]];
      if (code >= NUCLEOTIDE_CODE_COUNT) continue;
      codeBuffer[codeFilled] = code;
      codeFilled += 1;
      basesRead += 1;
      if (basesRead > maxLength) {
        throw new Error(`配列が長すぎます: ${name} は ${basesRead} bp（上限 ${maxLength} bp）`);
      }
      if (codeFilled === CODE_BUFFER_SIZE) await flushCodes();
    }
  };

  for await (const lineBytes of iterateLineBytes(source)) {
    bytesRead += lineBytes.length + 1;

    if (mode === 'origin') {
      if (lineBytes.length >= 2 && lineBytes[0] === 0x2f && lineBytes[1] === 0x2f) {
        await finishRecord();
      } else {
        await appendOriginLine(lineBytes);
      }
    } else {
      const line = decoder.decode(lineBytes);
      if (line.startsWith('//')) {
        await finishRecord();
      } else if (line.startsWith('ORIGIN')) {
        await emitPendingFeature();
        await openSequence();
        mode = 'origin';
      } else if (line.startsWith('FEATURES')) {
        await openSequence();
        mode = 'features';
      } else if (mode === 'features') {
        if (line.length > 0 && line[0] !== ' ') {
          // FEATURES の後に来る別セクション（BASE COUNT 等）
          await emitPendingFeature();
          mode = 'header';
          handleHeaderLine(line);
        } else {
          await handleFeatureLine(line);
        }
      } else {
        handleHeaderLine(line);
      }
    }

    if (bytesRead - lastProgressAt >= progressInterval) {
      lastProgressAt = bytesRead;
      reportProgress();
    }
  }

  await finishRecord();
  reportProgress();

  return { sequenceCount, bytesRead, warnings };
}
